import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  Search,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  // Key,
} from "lucide-react";
import { NLPQueryResult } from "../services/NLPQueryService";
import { api, QueryGeneratePayload } from "../common/api";
// import { api } from "../../common/api";

interface NLPQueryInputProps {
  onQueryGenerated: (sql: string, naturalQuery: string) => void;
  onExplanationRequested: (sql: string) => void;
}

const NLPQueryInput: React.FC<NLPQueryInputProps> = ({
  onQueryGenerated,
  // onExplanationRequested,
}) => {
  const [query, setQuery] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<NLPQueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showGeneratedSQL, setShowGeneratedSQL] = useState(false);
  // const [showApiKeyInput, setShowApiKeyInput] = useState(
  //   !localStorage.getItem("openai_api_key") &&
  //     !import.meta.env.VITE_OPENAI_API_KEY
  // );
  // const [isSubmittingApiKey, setIsSubmittingApiKey] = useState(false);

  const processQuery = async (queryText: string) => {
    if (!queryText.trim()) {
      toast.error("Please enter a query to analyze");
      return;
    }

    const connectionDetail = JSON.parse(
      localStorage.getItem("activeConnection") || "{}"
    );
    const connection_id = connectionDetail.connection_id || connectionDetail.id;
    // const connection_id = "2887b81e-08c1-478e-ad7d-b978a03215b1";

    if (!connection_id) {
      toast.error("Connection Id is required");
      return;
    }

    // Check for auth token
    const tokens = localStorage.getItem("token");
    if (tokens) {
      try {
        const parsedTokens = JSON.parse(tokens);
        const accessToken = parsedTokens.accessToken;
        if (accessToken) {
          api.setAuthToken(accessToken);
        } else {
          toast.error("Authentication required. Please log in again.");
          return;
        }
      } catch (error) {
        console.error("Failed to parse tokens:", error);
        toast.error("Authentication error. Please log in again.");
        return;
      }
    } else {
      toast.error("No authentication found. Please log in again.");
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const payload: QueryGeneratePayload = {
        connection_id,
        query: queryText,
      };

      console.log("Sending query to API:", payload);
      const response = await api.generateQuery(payload);
      console.log("API Response:", response);

      // Handle the actual backend response structure
      if (response && response.sql_query) {
        // Create NLPQueryResult structure from backend API response
        const nlpResult: NLPQueryResult = {
          sql: response.sql_query,
          query_summary: response.query_summary,
          alternative_queries: response.alternative_queries,
          explanation: `Query generated for database: ${
            response.database_name || "Unknown"
          } (${response.database_type || "Unknown type"})`,
          confidence: 0.9, // Backend doesn't provide confidence, set default high value
          tables: response.relevant_tables || [],
          fields: [], // Backend doesn't provide field details in this format
          conditions: [], // Backend doesn't provide condition details
        };

        setResult(nlpResult);
        setShowGeneratedSQL(true);
        onQueryGenerated(response.sql_query, queryText);
        toast.success("SQL query generated successfully!");

        // Clear any previous errors
        setError(null);
      } else {
        console.warn("Unexpected response structure:", response);
        setError("Unexpected response format from server");
        toast.error("Unexpected response format from server");
      }
    } catch (error: any) {
      console.error("Error generating query:", error);

      let errorMessage = "Failed to generate query. Please try again.";

      if (error.status === 401) {
        errorMessage = "Authentication failed. Please log in again.";
      } else if (error.status === 400) {
        // Handle specific backend validation errors
        if (
          error.message &&
          error.message.includes("connection_id is required")
        ) {
          errorMessage =
            "Database connection ID is missing. Please select a database connection.";
        } else if (
          error.message &&
          error.message.includes("query is required")
        ) {
          errorMessage =
            "Query text is required. Please enter a natural language query.";
        } else if (
          error.message &&
          error.message.includes("OpenAI API key not found")
        ) {
          errorMessage =
            "OpenAI API key not configured for this database connection. Please update your connection settings.";
        } else {
          errorMessage =
            error.message || "Invalid request. Please check your query.";
        }
      } else if (error.status === 404) {
        // Handle schema/connection not found errors
        if (
          error.message &&
          error.message.includes("No relevant schema found")
        ) {
          errorMessage =
            "No database schema found for this connection. Please ensure your database schema is properly indexed.";
        } else if (
          error.message &&
          error.message.includes("Connection not found")
        ) {
          errorMessage =
            "Database connection not found. Please check your connection settings.";
        } else {
          errorMessage =
            "Connection not found. Please check your database connection.";
        }
      } else if (error.status === 500) {
        errorMessage =
          "Server error occurred while processing your query. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }

    // try {
    //   const nlpService = new NLPQueryService(currentApiKey);

    //   // Mock data for demo - in real app, get from database
    //   const schema = {
    //     tables: {
    //       water_readings: {
    //         id: "SERIAL PRIMARY KEY",
    //         station_id: "VARCHAR(50)",
    //         reading_time: "TIMESTAMP",
    //         temperature: "DECIMAL",
    //         ph_level: "DECIMAL",
    //         dissolved_oxygen: "DECIMAL",
    //       },
    //     },
    //   };

    //   const sampleData = {
    //     water_readings: [
    //       { id: 1, station_id: "ST001", temperature: 23.5, ph_level: 7.2 },
    //     ],
    //   };

    //   const relationships = {};
    //   const dbType = "postgresql";

    //   const result = await nlpService.generateSQLFromNaturalLanguage(
    //     queryText,
    //     JSON.stringify(schema),
    //     JSON.stringify(sampleData),
    //     JSON.stringify(relationships),
    //     dbType
    //   );
    //   setResult(result);
    //   onQueryGenerated(result.sql, queryText);

    //   // Get related query suggestions
    //   const relatedQueries = await nlpService.suggestRelatedQueries(
    //     queryText,
    //     []
    //   );
    //   setSuggestions(relatedQueries);
    // } catch (error: any) {
    //   console.error("Error analyzing query:", error);
    //   if (error?.error?.code === "invalid_api_key") {
    //     toast.error(
    //       "Invalid OpenAI API key. Please check your key and try again."
    //     );
    //     // setShowApiKeyInput(true);
    //   } else {
    //     toast.error(
    //       "An error occurred while analyzing the query. Please try again."
    //     );
    //   }
    // } finally {
    //   setIsAnalyzing(false);
    // }
  };

  const handleQueryAnalyze = async () => {
    if (!query.trim()) {
      toast.error("Please enter a query to analyze");
      return;
    }

    // Clear previous results before starting new analysis
    setResult(null);
    setShowGeneratedSQL(false);
    setError(null);

    await processQuery(query);
  };

  const handleClearQuery = () => {
    setQuery("");
    setShowGeneratedSQL(false);
    setResult(null);
    setError(null);
  };

  // const handleApiKeySubmit = async (e: React.FormEvent) => {
  //   e.preventDefault(); // Prevent form refresh

  //   if (!apiKey || !apiKey.trim()) {
  //     setError("Please enter a valid API key");
  //     return;
  //   }

  //   // Basic API key validation (OpenAI keys start with 'sk-')
  //   if (!apiKey.startsWith("sk-")) {
  //     setError(
  //       "Invalid API key format. OpenAI API keys should start with 'sk-'"
  //     );
  //     return;
  //   }

  //   setIsSubmittingApiKey(true);
  //   setError(null);

  //   try {
  //     console.log("Starting API key submission process...");
  //     const tokens = localStorage.getItem("token");
  //     console.log("Tokens from localStorage:", tokens ? "Found" : "Not found");

  //     let accessToken = null;

  //     if (tokens) {
  //       try {
  //         const parsedTokens = JSON.parse(tokens);
  //         accessToken = parsedTokens.accessToken;
  //         console.log(
  //           "Extracted access token:",
  //           accessToken ? "Found" : "Not found"
  //         );
  //       } catch (error) {
  //         console.error("Failed to parse tokens from localStorage:", error);
  //         setError("Authentication error. Please log in again.");
  //         return;
  //       }
  //     }

  //     if (!accessToken) {
  //       console.warn("No access token found");
  //       setError("No authentication token found. Please log in again.");
  //       return;
  //     }

  //     api.setAuthToken(accessToken);

  //     const connectionDetail = localStorage.getItem("activeConnection");
  //     let connectionId = null;

  //     if (connectionDetail) {
  //       try {
  //         const parsedConnectionsDetail = JSON.parse(connectionDetail);
  //         // Try both possible property names for connection ID
  //         connectionId =
  //           parsedConnectionsDetail.id ||
  //           parsedConnectionsDetail.connection_id ||
  //           parsedConnectionsDetail.connectionId;
  //         console.log("Extracted connection ID:", connectionId);
  //       } catch (error) {
  //         console.error("Failed to parse connection details:", error);
  //         setError(
  //           "Invalid connection details. Please reconnect to your database."
  //         );
  //         return;
  //       }
  //     }

  //     if (!connectionId) {
  //       setError(
  //         "No active database connection found. Please connect to a database first."
  //       );
  //       return;
  //     }

  //     console.log("Saving API key for connection:", connectionId);
  //     const response = await api.openAPIConnection({
  //       connection_id: connectionId,
  //       openai_key: apiKey,
  //     });

  //     console.log("API key saved successfully:", response);

  //     // Store the API key for future use
  //     localStorage.setItem("openai_api_key", apiKey);

  //     // Hide the API key input and show success
  //     setShowApiKeyInput(false);
  //     setError(null);
  //   } catch (error: any) {
  //     console.error("Failed to save API key:", error);

  //     let errorMessage = "Failed to save API key. Please try again.";

  //     if (error.status === 401) {
  //       errorMessage = "Authentication failed. Please log in again.";
  //     } else if (error.status === 400) {
  //       errorMessage =
  //         "Invalid API key or connection. Please check your inputs.";
  //     } else if (error.message) {
  //       errorMessage = error.message;
  //     }

  //     setError(errorMessage);
  //   } finally {
  //     setIsSubmittingApiKey(false);
  //   }
  // };

  // if (showApiKeyInput) {
  //   return;
  // (
  // <div className="bg-gray-800 rounded-lg p-6">
  //   <div className="flex items-center gap-2 mb-4">
  //     <Key className="w-5 h-5 text-blue-400" />
  //     <h3 className="text-lg font-medium">OpenAI API Key Required</h3>
  //   </div>

  //   {error && (
  //     <div className="mb-4 p-3 bg-red-500/20 text-red-400 rounded-lg flex items-center gap-2">
  //       <AlertTriangle className="w-4 h-4" />
  //       {error}
  //     </div>
  //   )}

  //   <form onSubmit={handleApiKeySubmit} className="space-y-4">
  //     <div>
  //       <label
  //         htmlFor="apiKey"
  //         className="block text-sm font-medium text-gray-400 mb-2"
  //       >
  //         Enter your OpenAI API key to enable natural language query
  //         processing
  //       </label>
  //       <input
  //         type="password"
  //         id="apiKey"
  //         value={apiKey}
  //         onChange={(e) => setApiKey(e.target.value)}
  //         disabled={isSubmittingApiKey}
  //         className="w-full bg-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
  //         placeholder="sk-..."
  //         required
  //       />
  //     </div>

  //     <div className="flex items-center justify-between">
  //       <p className="text-sm text-gray-400">
  //         Get your API key from{" "}
  //         <a
  //           href="https://platform.openai.com/account/api-keys"
  //           target="_blank"
  //           rel="noopener noreferrer"
  //           className="text-blue-400 hover:text-blue-300"
  //         >
  //           OpenAI's dashboard
  //         </a>
  //       </p>
  //       <button
  //         type="submit"
  //         disabled={!apiKey || isSubmittingApiKey}
  //         className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:opacity-50 px-6 py-2 rounded-lg flex items-center gap-2"
  //       >
  //         {isSubmittingApiKey && (
  //           <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
  //         )}
  //         {isSubmittingApiKey ? "Saving..." : "Save Key"}
  //       </button>
  //     </div>
  //   </form>
  // </div>
  // );
  // }

  return (
    <div className="space-y-4">
      {/* API Key Management */}
      {/* {apiKey && !showApiKeyInput && ( */}

      {/* <div className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-400" />
          <span className="text-sm text-gray-300">
            OpenAI API key configured
          </span>
        </div>
        <button
          // onClick={() => setShowApiKeyInput(true)}
          className="text-sm text-blue-400 hover:text-blue-300"
        >
          Change Key
        </button>
      </div> */}
      {/* )} */}

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              showGeneratedSQL
                ? "Generated SQL query"
                : "Describe your query in plain English..."
            }
            className={`w-full rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
              showGeneratedSQL
                ? "bg-green-900/20 border border-green-400"
                : "bg-gray-800"
            }`}
          />
        </div>
        <button
          onClick={handleQueryAnalyze}
          disabled={!query || isAnalyzing}
          className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 px-6 py-2 rounded-lg flex items-center gap-2"
        >
          {isAnalyzing ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
          ) : (
            <Sparkles className="w-5 h-5" />
          )}
          Analyze
        </button>
        {showGeneratedSQL && (
          <button
            onClick={handleClearQuery}
            className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg text-sm"
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-500/20 text-red-400 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {result && (
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {result.confidence > 0.8 ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : result.confidence > 0.5 ? (
                <HelpCircle className="w-5 h-5 text-yellow-400" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              )}
              <span className="font-medium">
                Translation Confidence: {Math.round(result.confidence * 100)}%
              </span>
            </div>
          </div>

          <div className="space-y-4 mt-4">
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-2">
                Generated SQL
              </h4>
              <pre className="bg-gray-900 p-3 rounded-lg text-sm text-green-400 overflow-x-auto">
                <code>{result.sql}</code>
              </pre>
              <h4 className="text-sm font-medium text-gray-400 mb-2 ">
                Query Summary
              </h4>
              <pre className="bg-gray-900 p-3 rounded-lg text-sm text-green-400 overflow-x-auto">
                <code>{result.query_summary}</code>
              </pre>
            </div>
          </div>

          {result.alternative_queries &&
            result.alternative_queries.length > 0 && (
              <div className="space-y-4 mt-4">
                <h4 className="font-medium">Alternative Queries</h4>
                {result.alternative_queries.map((alt: any, idx: number) => (
                  <div
                    key={idx}
                    className="bg-gray-900 p-3 rounded-lg text-sm text-blue-300"
                  >
                    <div>
                      <span className="font-semibold">SQL:</span>
                      <pre className="bg-gray-800 p-2 rounded mt-1 text-green-400 overflow-x-auto">
                        <code>{alt.query}</code>
                      </pre>
                    </div>
                    {alt.summary && (
                      <div>
                        <span className="font-semibold">Summary:</span>
                        <pre className="bg-gray-800 p-2 rounded mt-1 text-gray-300 overflow-x-auto">
                          <code>{alt.summary}</code>
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default NLPQueryInput;
